import { useEffect, useRef, useState } from 'react';

export const COLOR_OPTIONS = [
   { value: '#e74c3c', label: 'Red' },
   { value: '#e67e22', label: 'Orange' },
   { value: '#f1c40f', label: 'Yellow' },
   { value: '#2ecc71', label: 'Green' },
   { value: '#1abc9c', label: 'Teal' },
   { value: '#3498db', label: 'Blue' },
   { value: '#5c6bc0', label: 'Indigo' },
   { value: '#9b59b6', label: 'Purple' },
   { value: '#e91e63', label: 'Pink' },
   { value: '#95a5a6', label: 'Gray' },
   { value: '#ffffff', label: 'White' },
];

export interface StructureColorComponentProps {
   selectedColor: string;
   changeColor: (color: string) => void;
   title: string;
}

/**
 * A dropdown panel of color swatches for picking the uniform fill color of a
 * structure component. The currently active swatch is visually highlighted.
 *
 * Closes on outside click (document-level `mousedown` listener). Used inside
 * `StructureRepresentationControlComponent` for the chain, pocket, and ligand
 * color pickers.
 */
const StructureColorComponent = (props: StructureColorComponentProps): React.JSX.Element => {
   const [open, setOpen] = useState(false);
   const ref = useRef<HTMLDivElement>(null);

   useEffect(() => {
      const handler = (e: MouseEvent): void => {
         if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
      };
      document.addEventListener('mousedown', handler);
      return (): void => document.removeEventListener('mousedown', handler);
   }, []);

   return (
      <div className="repr-dropdown repr-dropdown-color" ref={ref}>
         <button className="repr-dropdown-btn repr-dropdown-btn-color" onClick={(): void => setOpen((o) => !o)}>
            <span className="repr-dropdown-label">
               <span className="repr-dropdown-title">{props.title}</span>
               <span className="repr-dropdown-sep">:</span>
            </span>
            <span className="repr-dropdown-color-right">
               <span className="repr-dropdown-chevron">{open ? '▴' : '▾'}</span>
               <span className="color-swatch-preview" style={{ background: props.selectedColor }} />
            </span>
         </button>
         {open && (
            <div className="repr-dropdown-menu color-swatch-grid">
               {COLOR_OPTIONS.map((opt) => (
                  <button
                     key={opt.value}
                     className={`color-swatch-item${opt.value === props.selectedColor ? ' active' : ''}`}
                     title={opt.label}
                     style={{ background: opt.value }}
                     onClick={(): void => {
                        props.changeColor(opt.value);
                        setOpen(false);
                     }}
                  />
               ))}
            </div>
         )}
      </div>
   );
};

export default StructureColorComponent;
